<template>
  <div class="fixed inset-0 bg-white z-[1050] flex flex-col h-screen overflow-hidden digital-sign-config-wrapper">
    <!-- Fullscreen Header -->
    <div class="bg-minplan-blue text-white px-6 py-4 flex justify-between items-center shadow-md">
      <div>
        <h2 class="text-xl font-bold text-white">Digitale Signatur einrichten</h2>
        <p class="text-xs opacity-80 text-white">{{ fileName }}</p>
      </div>
      <div class="flex items-center gap-4">
        <button type="button" @click="$emit('cancel')" class="px-4 py-2 bg-white bg-opacity-10 hover:bg-opacity-20 text-white rounded font-bold transition">
          Abbrechen
        </button>
      </div>
    </div>

    <div class="flex-1 flex overflow-hidden">
      <!-- Sidebar Settings -->
      <div class="w-80 flex-shrink-0 bg-gray-50 border-r p-6 overflow-y-auto flex flex-col h-full shadow-inner">
        
        <div class="mb-6">
          <label class="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-tight">1. Unterzeichner konfigurieren</label>
          <div v-if="!isAdminSignOnly" class="bg-white border rounded-lg p-3 shadow-sm mb-4">
            <label v-if="!selfSigned" class="flex items-center gap-2 cursor-pointer mb-2">
              <input type="checkbox" v-model="selfSignFirst" class="rounded text-minplan-blue focus:ring-minplan-blue" />
              <span class="text-sm font-medium">Zuerst selbst unterschreiben</span>
            </label>
            <div v-if="selfSignFirst" class="text-[10px] text-blue-600 font-semibold mb-2 p-1 bg-blue-50 rounded">
                <p v-if="!selfSigned">Schritt 1: Platzieren Sie Ihre Signaturfelder und klicken Sie auf „Jetzt unterschreiben“.</p>
                <p v-else>Schritt 1: Abgeschlossen. Nun Käufer zuweisen.</p>
            </div>
          </div>

          <div v-if="isAdminSignOnly" class="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-6">
             <p class="text-[11px] text-blue-800 font-bold leading-tight">Admin-Modus: Signaturfeld platzieren und unterschreiben. Keine Käufer beteiligt.</p>
          </div>

          <label v-if="!isAdminSignOnly" class="block text-sm font-bold text-gray-700 mb-2">Käufer auswählen</label>
          <div v-if="!isAdminSignOnly" class="space-y-2">
             <div v-if="selfSignFirst && !selfSigned" class="text-sm text-gray-500 bg-gray-100 p-3 rounded border border-dashed text-center">
               <span class="block font-medium mb-1">Eigene Signatur aktiv</span>
               <span class="text-[10px]">Käufer können ausgewählt werden, nachdem Sie Ihre Signatur abgeschlossen haben.</span>
             </div>
             <div v-else class="space-y-3">
                <div class="relative group buyer-multiselect-container">
                  <div class="flex flex-wrap gap-1 p-2 bg-white border border-gray-300 rounded-lg shadow-sm min-h-[42px] focus-within:ring-2 focus-within:ring-minplan-blue focus-within:border-minplan-blue transition cursor-pointer" @click="showBuyerDropdown = !showBuyerDropdown">
                    <div v-if="selectedBuyerIds.length === 0" class="text-sm text-gray-400 py-1 px-2">Einen oder mehrere Käufer auswählen...</div>
                    <div v-for="id in selectedBuyerIds" :key="'pill-'+id" class="bg-minplan-blue text-white text-[10px] px-2 py-1 rounded-full flex items-center gap-1 group/pill shadow-sm">
                      <span class="font-bold">{{ buyerLabel(id) }}</span>
                      <button @click.stop="toggleBuyer(id)" class="hover:text-red-300 font-bold ml-1 text-xs">×</button>
                    </div>
                    <div class="absolute right-3 top-3 pointer-events-none">
                      <svg class="h-4 w-4 text-gray-400 transition" :class="{'rotate-180': showBuyerDropdown}" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" /></svg>
                    </div>
                  </div>

                  <!-- Multiselect Dropdown -->
                  <div v-if="showBuyerDropdown" class="absolute z-[101] left-0 right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-xl max-h-80 flex flex-col overflow-hidden">
                    <div class="p-2 border-b bg-gray-50">
                      <input 
                        type="text" 
                        v-model="buyerSearch" 
                        placeholder="Käufer suchen" 
                        class="w-full px-3 py-1.5 text-xs border rounded focus:ring-1 focus:ring-minplan-blue outline-none"
                        @click.stop
                      />
                    </div>
                    <div class="overflow-y-auto flex-1">
                      <div v-if="filteredBuyersForDropdown.length === 0" class="p-4 text-center text-sm text-gray-400 italic">Keine passenden Käufer.</div>
                      <div v-for="b in filteredBuyersForDropdown" :key="b.id" @click="toggleBuyer(b.id)" class="flex items-center gap-3 p-3 hover:bg-minplan-blue hover:bg-opacity-10 border-b last:border-b-0 group/item transition" :class="b.invited === false ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'">
                        <div class="w-5 h-5 flex items-center justify-center border-2 rounded transition" :class="selectedBuyerIds.includes(b.id) ? 'bg-minplan-blue border-minplan-blue' : 'border-gray-300'">
                          <svg v-if="selectedBuyerIds.includes(b.id)" class="h-3 w-3 text-white" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" /></svg>
                        </div>
                        <div class="leading-tight">
                          <div class="text-[13px] font-bold text-gray-800">
                            {{ (b.firstname || b.name || 'Unterzeichner') }} {{ (b.lastname || '') }}
                          </div>
                          <div class="text-[10px] text-gray-500 font-bold opacity-80">
                            {{ b.email || '(Keine E-Mail angegeben)' }}
                          </div>
                          <!-- Spec §3b: ein vorbereiteter (noch nicht eingeladener)
                               Käufer bleibt sichtbar, ist aber nicht auswählbar —
                               `toggleBuyer()` weist ihn zusätzlich serverseitig ab. -->
                          <div v-if="b.invited === false" class="text-[9px] text-red-500 font-bold uppercase mt-0.5">
                            {{ $t('documents.buyerNotInvitedBadge') }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
             </div>
          </div>
        </div>

        <div class="mb-6">
          <label class="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-tight">2. Aktionen</label>
          <div class="grid grid-cols-1 gap-2">
            <button @click="clearAll" class="w-full text-left px-4 py-2 border border-gray-300 rounded-lg hover:bg-white transition flex items-center justify-between group">
              <span class="text-sm font-medium text-gray-600">Alle Elemente entfernen</span>
              <svg class="h-4 w-4 text-gray-400 group-hover:text-red-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
            </button>
            <button @click="copyAllToAllPages" class="w-full text-left px-4 py-2 border border-gray-300 rounded-lg hover:bg-white transition flex items-center justify-between group" title="Alle Felder von der aktuellen Seite auf alle anderen Seiten kopieren">
              <span class="text-sm font-medium text-gray-600">Elemente auf alle Seiten kopieren</span>
              <svg class="h-4 w-4 text-gray-400 group-hover:text-purple-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7v8a2 2 0 002 2h6M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414a1 1 0 01.293.707V15a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2" />
              </svg>
            </button>
          </div>
        </div>

        <div class="mt-auto border-t pt-4">
           <div v-if="selfSignFirst" class="mb-4">
              <button @click="selfSignNow" :disabled="selfWidgets.length === 0 || selfSigning || selfSigned" 
                      class="w-full py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-bold disabled:opacity-50 flex items-center justify-center gap-2 shadow-sm transition">
                <span v-if="!selfSigned">{{ selfSigning ? 'Wird verarbeitet...' : 'Jetzt unterschreiben' }}</span>
                <span v-else>Eigene Signatur abgeschlossen</span>
              </button>
              <p v-if="selfWidgets.length === 0 && !selfSigned" class="text-[10px] text-red-500 mt-1 text-center font-medium italic">Zeichnen Sie zuerst mindestens 1 Feld für sich selbst.</p>
           </div>

            <button v-if="!(selfSignFirst && !selfSigned)" @click="send" 
                    :disabled="sending || (!isAdminSignOnly && (selectedBuyerIds.length === 0 || widgets.length === 0 || !allAssigned)) || (isAdminSignOnly && !selfSigned)" 
                    class="w-full py-4 bg-minplan-blue hover:bg-minplan-blue-dark text-white rounded-lg font-bold shadow-lg disabled:opacity-40 disabled:cursor-not-allowed transition">
             {{ sending ? 'Speichern...' : (isAdminSignOnly ? 'Admin-Signatur abschliessen' : 'Abschliessen') }}
           </button>
           <p v-if="!isAdminSignOnly && widgets.length === 0 && (selfSigned || !selfSignFirst)" class="text-[10px] text-red-500 mt-1 text-center font-medium italic">Platzieren Sie Signaturfelder für die Käufer, bevor Sie speichern.</p>
           <p v-if="isAdminSignOnly && !selfSigned" class="text-[10px] text-blue-600 mt-1 text-center font-medium italic">Eigenhändige Unterschrift ist vor dem Abschluss erforderlich.</p>
        </div>
      </div>

      <!-- Main Canvas Area -->
      <div class="flex-1 flex flex-col bg-gray-200">
        <!-- Canvas Header / Pagination -->
        <div class="bg-white border-b px-6 py-2 flex items-center justify-between shadow-sm">
           <div class="flex items-center gap-3">
             <button @click="prevPage" :disabled="currentPage <= 1 || loading" class="p-2 hover:bg-gray-100 rounded-full disabled:opacity-30">
               <svg class="h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" /></svg>
             </button>
             <span class="text-sm font-bold min-w-[100px] text-center">Seite {{ currentPage }} / {{ totalPages }}</span>
             <button @click="nextPage" :disabled="currentPage >= totalPages || loading" class="p-2 hover:bg-gray-100 rounded-full disabled:opacity-30">
               <svg class="h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" /></svg>
             </button>
           </div>
           <div class="text-xs text-gray-500 font-medium font-bold">
             Tipp: Klicken und ziehen Sie im Dokument, um ein Signatur- oder Namensfeld zu platzieren.
           </div>
        </div>

        <div v-if="loading" class="flex-1 flex items-center justify-center">
          <div class="flex flex-col items-center gap-4">
              <svg class="animate-spin h-10 w-10 text-minplan-blue" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
              <p class="text-gray-600 font-bold uppercase tracking-widest animate-pulse">Rendering PDF Interface...</p>
          </div>
        </div>

        <div v-show="!loading" class="flex-1 overflow-auto p-8 flex justify-center">
          <div ref="previewContainer" class="relative inline-block border bg-white shadow-2xl transition-all duration-300">
            <canvas ref="canvas" class="block mx-auto"></canvas>
            <div
              ref="overlay"
              class="absolute inset-x-0 mx-auto top-0 cursor-crosshair z-20"
              :style="{ width: canvasWidth + 'px', height: canvasHeight + 'px', left: overlayLeft + 'px' }"
              @mousedown="startAction"
              @mousemove="onMove"
              @mouseup="endAction"
              @mouseleave="endAction"
            >
              <div v-if="action === 'draw'" :style="drawingStyle"></div>
              
              <!-- Self Sign Widgets -->
              <div
                v-for="(sw, sidx) in currentPageSelfWidgets"
                :key="'self-'+sidx"
                class="absolute border-2 border-emerald-600 bg-emerald-100 bg-opacity-30 flex items-center justify-center group z-30 shadow-md"
                :style="getWidgetStyle(sw)"
                @mousedown.stop="startMoveSelf(sw, $event)"
              >
                <span class="text-[10px] text-emerald-800 font-extrabold uppercase select-none">Ich (Unterzeichner)</span>
                <div class="absolute -top-3 left-0 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                   <button @click.stop="toggleNameSelf(sw)" class="bg-gray-800 text-white rounded px-2 h-5 flex items-center text-[10px] shadow" title="Namenszusatz umschalten">
                     {{ sw.showName ? '− Name' : '+ Name' }}
                   </button>
                   <button @click.stop="copySelfWidgetAllPages(sw)" class="bg-purple-600 text-white rounded px-2 h-5 flex items-center text-[10px] shadow">Kopieren</button>
                   <button @click.stop="removeSelfWidget(sw)" class="bg-red-500 text-white rounded px-2 h-5 flex items-center shadow font-bold" title="Löschen"><svg class="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg></button>
                </div>
                <div v-if="sw.showName" class="absolute left-0 top-full mt-1 border border-emerald-400 bg-white bg-opacity-90 px-2 py-0.5 text-[10px] text-emerald-700 flex items-center justify-between whitespace-nowrap shadow-sm font-bold" :style="{ width: '100%', height: Math.round(21 * (viewportScale || 1)) + 'px' }">
                  <span>Name: {{ $page.props.user.firstname }}</span>
                </div>
                <div v-if="selectedSelfWidget === sw" class="absolute bottom-[-5px] right-[-5px] w-4 h-4 bg-emerald-600 border border-white rounded-full cursor-se-resize shadow-sm" @mousedown.stop="startResizeSelf(sw, $event)"></div>
              </div>

              <!-- Buyer Widgets -->
              <div
                v-for="(widget, index) in currentPageWidgets"
                :key="index"
                class="absolute border-2 flex items-center justify-center group shadow-md z-30"
                :class="selectedWidget === widget ? 'border-blue-600 bg-blue-100 bg-opacity-40 z-40' : 'border-blue-500 bg-white bg-opacity-30'"
                :style="getWidgetStyle(widget)"
                @mousedown.stop="startMove(widget, $event)"
              >
                <span class="text-[10px] font-extrabold uppercase select-none p-1 truncate max-w-full" :class="badgeClass(widget)">
                  {{ buyerLabel(widget.buyer_id) || '!! UNTERZEICHNER AUSWÄHLEN !!' }}
                </span>
                <div class="absolute -top-3 left-0 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-50">
                   <button @click.stop="toggleName(widget)" class="bg-gray-800 text-white rounded px-2 h-5 flex items-center text-[10px] shadow">
                     {{ widget.showName ? '− Name' : '+ Name' }}
                   </button>
                   <button @click.stop="copyWidgetAllPages(widget)" class="bg-purple-600 text-white rounded px-2 h-5 flex items-center text-[10px] shadow">Kopieren</button>
                   <button @click.stop="removeWidget(widget)" class="bg-red-500 text-white rounded px-2 h-5 flex items-center shadow font-bold" title="Löschen"><svg class="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg></button>
                </div>
                
                <div v-if="selectedWidget === widget && !action && selectedBuyerIds.length > 1" class="absolute top-[calc(100%+12px)] left-0 bg-white border border-blue-400 p-1.5 rounded shadow-xl z-[1000] w-56" @mousedown.stop>
                   <p class="text-[9px] uppercase font-bold text-blue-600 mb-1 tracking-tight">Unterzeichner zuweisen:</p>
                   <select v-model.number="widget.buyer_id" class="text-[11px] bg-white border border-gray-300 rounded w-full py-1 px-1.5 focus:ring-1 focus:ring-blue-500 outline-none shadow-sm cursor-pointer">
                    <option v-for="b in assignableBuyers" :key="b.id" :value="b.id" :class="selectedBuyerIds.includes(b.id) ? 'font-bold' : 'text-gray-400'">
                        {{ (b.firstname || b.name || 'Unterzeichner') }} {{ (b.lastname || '') }}
                        {{ !selectedBuyerIds.includes(b.id) ? ' (Nicht ausgewählt)' : '' }}
                    </option>
                  </select>
                  <p v-if="hasUninvitedBuyers" class="text-[9px] text-gray-400 italic mt-1 leading-tight">
                    {{ $t('documents.uninvitedBuyersHidden') }}
                  </p>
                </div>

                <div v-if="widget.showName" class="absolute left-0 top-full mt-1 border border-blue-400 bg-white bg-opacity-90 px-2 py-0.5 text-[10px] text-gray-700 flex items-center justify-between whitespace-nowrap shadow-sm font-bold truncate" :style="{ width: '100%', height: Math.round(21 * (viewportScale || 1)) + 'px' }">
                  <span>Name: {{ buyerLabel(widget.buyer_id) }}</span>
                </div>
                <div v-if="selectedWidget === widget" class="absolute bottom-[-6px] right-[-6px] w-5 h-5 bg-blue-600 border-2 border-white rounded-full cursor-se-resize shadow-md z-[1001]" @mousedown.stop="startResize(widget, $event)"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Self Sign Iframe Full Depth -->
    <div v-if="selfShowModal" class="fixed inset-0 bg-black z-[10000] flex flex-col h-screen overflow-hidden">
        <div class="bg-emerald-700 p-4 flex justify-between items-center text-white shadow-lg">
           <div>
             <h3 class="font-bold text-lg text-white">Elektronische Signatur</h3>
             <p class="text-xs opacity-80 italic text-white">Unterzeichnen Sie das Dokument unten. Schliessen Sie dieses Fenster erst, wenn Sie fertig sind.</p>
           </div>
           <div class="flex gap-2">
             <button @click="checkSelfComplete" class="px-6 py-2 bg-white text-emerald-800 font-extrabold rounded-lg shadow-sm hover:bg-emerald-50 transition">
               Ich habe die Unterzeichnung abgeschlossen
             </button>
             <button @click="selfShowModal = false" class="px-4 py-2 border border-white border-opacity-30 rounded-lg hover:bg-white hover:bg-opacity-10 transition text-white">
               Fenster schliessen
             </button>
           </div>
        </div>
        <iframe :src="selfSignUrl" class="flex-1 w-full bg-white border-none shadow-inner"></iframe>
    </div>
  </div>
</template>

<script>
import { defineComponent, ref, nextTick } from 'vue'
import axios from 'axios'

export default defineComponent({
  name: 'DigitalSignatureConfig',
  props: {
    buyers: { type: Array, default: () => [] },
    pdfBase64: String,
    fileName: String,
    authUserId: [String, Number],
    isAdminSignOnly: { type: Boolean, default: false },
    maxBuyerSelect: { type: Number, default: null }
  },
  emits: ['cancel', 'done'],
  data() {
    return {
      selectedBuyerIds: [],
      loading: false,
      canvas: null,
      overlay: null,
      pdfDoc: null,
      currentPage: 1,
      totalPages: 1,
      viewportScale: 1,
      action: null,
      start: { x: 0, y: 0 },
      current: { x: 0, y: 0 },
      selectedWidget: null,
      initialWidgetState: null,
      widgets: [],
      sending: false,
      selfSignFirst: false,
      selfWidgets: [],
      selfSigning: false,
      selfSigned: false,
      selfSignUrl: null,
      selfSignAt: null,
      selfDocId: null,
      selfShowModal: false,
      selectedSelfWidget: null,
      initialSelfState: null,
      canvasWidth: 0,
      canvasHeight: 0,
      overlayLeft: 0,
      addNameChecked: false,
      pdfBlobContainer: null,
      showBuyerDropdown: false,
      buyerSearch: '',
      selfPollingInterval: null
    }
  },
  computed: {
    selectedBuyers() {
      // Use loose equality to be safe with string vs int IDs
      return this.buyers.filter(b => this.selectedBuyerIds.some(sid => sid == b.id))
    },
    /**
     * Spec §3b: ein vorbereiteter (noch nicht eingeladener) Käufer darf
     * nicht als Unterzeichner zugewiesen werden — zusätzlich zum
     * Serverriegel in `sendMulti`, nicht statt seiner. `invited` fehlt an
     * manchen aufrufenden Seiten (noch) im Payload; dort bleibt die Liste
     * unverändert (kein `false`, also sichtbar), damit kein bestehender
     * Weg dadurch leerläuft.
     */
    assignableBuyers() {
      return this.buyers.filter(b => b.invited !== false)
    },
    hasUninvitedBuyers() {
      return this.assignableBuyers.length < this.buyers.length
    },
    allAssigned() {
      return this.widgets.every(w => !!w.buyer_id)
    },
    filteredBuyersForDropdown() {
       if(!this.buyerSearch) return this.buyers;
       const q = this.buyerSearch.toLowerCase();
       return this.buyers.filter(b => {
          const searchStr = [b.firstname, b.lastname, b.name, b.email]
            .filter(Boolean)
            .join(' ')
            .toLowerCase();
          return searchStr.includes(q);
       });
    },
    currentPageWidgets() {
      return this.widgets.filter(w => w.page === this.currentPage)
    },
    currentPageSelfWidgets() {
      const show = this.selfSignFirst || (!this.selfSigned && this.selfWidgets.length > 0)
      if (!show) return []
      return this.selfWidgets.filter(w => w.page === this.currentPage)
    },
    drawingStyle() {
      const sx = Math.min(this.start.x, this.current.x)
      const sy = Math.min(this.start.y, this.current.y)
      const sw = Math.abs(this.start.x - this.current.x)
      const sh = Math.abs(this.start.y - this.current.y)
      return {
        position: 'absolute',
        left: sx + 'px',
        top: sy + 'px',
        width: sw + 'px',
        height: sh + 'px',
        border: '3px dashed #2563eb',
        background: 'rgba(37,99,235,0.15)',
        zIndex: 50
      }
    }
  },
  watch: {
    pdfBase64(newVal) {
      if (newVal) {
        this.pdfBlobContainer = newVal
        this.loadPdf(newVal)
      }
    },
    selfShowModal(newVal) {
      if (newVal) {
        this.startSelfPolling();
      } else {
        this.stopSelfPolling();
      }
    }
  },
  beforeUnmount() {
    this.stopSelfPolling();
  },
  created() {
     if (this.isAdminSignOnly) {
         this.selfSignFirst = true;
     }
  },
  mounted() {
    this.canvas = this.$refs.canvas
    this.overlay = this.$refs.overlay
    if (this.pdfBase64) {
      this.pdfBlobContainer = this.pdfBase64
      this.loadPdf(this.pdfBase64)
    }
    console.log('DigitalSignatureConfig mounted. Buyers:', this.buyers);
    document.addEventListener('click', this.handleGlobalClick)
  },
  unmounted() {
    document.removeEventListener('click', this.handleGlobalClick)
  },
  methods: {
    handleGlobalClick(e) {
      const el = this.$el.querySelector('.buyer-multiselect-container')
      if (el && !el.contains(e.target)) {
        this.showBuyerDropdown = false
      }
    },
    buyerLabel(id) {
      if (!id) return null
      const b = this.buyers.find(x => x.id == id)
      if (!b) return 'Assign Signer'
      const first = b.firstname || b.name || 'Signer'
      const last = b.lastname || ''
      return `${first} ${last}`.trim()
    },
    badgeClass(widget) {
      const idx = this.selectedBuyerIds.findIndex(id => id === widget.buyer_id)
      const palette = ['bg-blue-600 text-white', 'bg-green-600 text-white', 'bg-purple-600 text-white', 'bg-pink-600 text-white', 'bg-yellow-600 text-white', 'bg-indigo-600 text-white']
      return 'px-1.5 py-0.5 rounded text-[10px] ' + (palette[idx % palette.length] || 'bg-gray-600 text-white')
    },
    base64ToUint8Array(base64) {
      const raw = atob(base64)
      const array = new Uint8Array(raw.length)
      for (let i = 0; i < raw.length; i++) array[i] = raw.charCodeAt(i)
      return array
    },
    async ensurePdfJs() {
      if (!window.pdfjsLib) {
        const script = document.createElement('script')
        script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.6.347/pdf.min.js'
        document.head.appendChild(script)
        await new Promise((resolve, reject) => {
          script.onload = resolve
          script.onerror = reject
        })
        window.pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.6.347/pdf.worker.min.js'
      }
      return window.pdfjsLib
    },
    async loadPdf(base64) {
      this.loading = true
      try {
        const lib = await this.ensurePdfJs()
        const data = this.base64ToUint8Array(base64)
        const loadingTask = lib.getDocument({ data })
        this.pdfDoc = await loadingTask.promise
        this.totalPages = this.pdfDoc.numPages
        this.currentPage = 1
        this.loading = false
        await nextTick()
        await this.renderPage(1)
      } catch (e) {
        console.error('PDF Load Error:', e)
        alert('PDF konnte nicht geladen werden')
        this.loading = false
      }
    },
    async renderPage(pageNum) {
      if (!this.pdfDoc) return
      const page = await this.pdfDoc.getPage(pageNum)
      const baseViewport = page.getViewport({ scale: 1 })
      const targetWidth = 800
      const scale = targetWidth / baseViewport.width
      const viewport = page.getViewport({ scale })
      const c = this.$refs.canvas
      if (!c) return
      c.width = viewport.width
      c.height = viewport.height
      c.style.width = viewport.width + 'px'
      c.style.height = viewport.height + 'px'
      this.canvasWidth = viewport.width
      this.canvasHeight = viewport.height
      this.viewportScale = scale
      const ctx = c.getContext('2d')
      await page.render({ canvasContext: ctx, viewport }).promise
      const parentWidth = c.parentElement?.clientWidth || 800
      this.overlayLeft = (parentWidth - viewport.width) / 2
    },
    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--
        this.renderPage(this.currentPage)
      }
    },
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++
        this.renderPage(this.currentPage)
      }
    },
    toggleBuyer(id) {
       const idx = this.selectedBuyerIds.indexOf(id);
       if(idx > -1) {
           this.selectedBuyerIds.splice(idx, 1);
           // Requirement: If a buyer is removed, remove all boxes assigned to them
           this.widgets = this.widgets.filter(w => w.buyer_id !== id);
           if (this.selectedWidget && this.selectedWidget.buyer_id === id) {
               this.selectedWidget = null;
           }
       } else {
            // Spec §3b: ein vorbereiteter (noch nicht eingeladener) Käufer
            // darf nicht als Unterzeichner ausgewählt werden — zusätzlich
            // zum Serverriegel in `sendMulti`, nicht statt seiner.
            const target = this.buyers.find(b => b.id == id);
            if (target && target.invited === false) {
               this.ensureSwal().then(Swal => Swal.fire(this.$t('documents.errorNotAllowed'), this.$t('documents.errorBuyerNotInvited'), 'warning'));
               return;
           }
            const maxBuyers = this.maxBuyerSelect || this.$page.props.document_digital_sign_max_buyer_select || 10;
            if (this.selectedBuyerIds.length >= maxBuyers) {
               this.ensureSwal().then(Swal => Swal.fire(this.$t('documents.errorLimitReached'), this.$t('documents.errorTooManyBuyers', { max: maxBuyers }), 'warning'));
               return;
           }
           this.selectedBuyerIds.push(id);
       }
    },
    toLocal(e) {
      const rect = this.$refs.overlay.getBoundingClientRect()
      return {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      }
    },
    startAction(e) {
      if (this.action) return
      
      const isSelfMode = (this.selfSignFirst && !this.selfSigned)
      
      if (!isSelfMode && this.selectedBuyerIds.length === 0) {
        this.ensureSwal().then(Swal => {
           Swal.fire({
             title: 'Bitte zuerst Käufer auswählen',
             text: 'Wählen Sie links einen Käufer aus, bevor Sie ein Signaturfeld platzieren.',
             icon: 'warning',
             confirmButtonColor: '#003366',
             confirmButtonText: 'Käufer auswählen'
           })
        })
        return
      }
      
      this.action = 'draw'
      this.selectedWidget = null
      const p = this.toLocal(e)
      this.start = p
      this.current = p
    },
    startMove(widget, e) {
      this.action = 'move'
      this.selectedWidget = widget
      this.initialWidgetState = { ...widget }
      this.selectedSelfWidget = null
      const p = this.toLocal(e)
      this.start = p
      this.current = p
    },
    startResize(widget, e) {
      this.action = 'resize'
      this.selectedWidget = widget
      this.initialWidgetState = { ...widget }
      this.selectedSelfWidget = null
      const p = this.toLocal(e)
      this.start = p
      this.current = p
    },
    onMove(e) {
      if (!this.action) return
      const p = this.toLocal(e)
      this.current = p
      const scale = this.viewportScale || 1
      if (this.action === 'move' && this.selectedWidget) {
        const dx = (this.current.x - this.start.x) / scale
        const dy = (this.current.y - this.start.y) / scale
        this.selectedWidget.x = Math.round(this.initialWidgetState.x + dx)
        this.selectedWidget.y = Math.round(this.initialWidgetState.y + dy)
      } else if (this.action === 'resize' && this.selectedWidget) {
        const dx = (this.current.x - this.start.x) / scale
        const dy = (this.current.y - this.start.y) / scale
        this.selectedWidget.w = Math.max(20, Math.round(this.initialWidgetState.w + dx))
        this.selectedWidget.h = Math.max(20, Math.round(this.initialWidgetState.h + dy))
      } else if (this.action === 'moveSelf' && this.selectedSelfWidget) {
        const dx = (this.current.x - this.start.x) / scale
        const dy = (this.current.y - this.start.y) / scale
        this.selectedSelfWidget.x = Math.round(this.initialSelfState.x + dx)
        this.selectedSelfWidget.y = Math.round(this.initialSelfState.y + dy)
      } else if (this.action === 'resizeSelf' && this.selectedSelfWidget) {
        const dx = (this.current.x - this.start.x) / scale
        const dy = (this.current.y - this.start.y) / scale
        this.selectedSelfWidget.w = Math.max(20, Math.round(this.initialSelfState.w + dx))
        this.selectedSelfWidget.h = Math.max(20, Math.round(this.initialSelfState.h + dy))
      }
    },
    endAction() {
      if (!this.action) return
      if (this.action === 'draw') {
        const sx = Math.min(this.start.x, this.current.x)
        const sy = Math.min(this.start.y, this.current.y)
        const sw = Math.abs(this.start.x - this.current.x)
        const sh = Math.abs(this.start.y - this.current.y)
        if (sw > 10 && sh > 10) {
          const scale = this.viewportScale || 1
          const pdfX = Math.round(sx / scale)
          const pdfY = Math.round(sy / scale)
          const pdfW = Math.round(sw / scale)
          const pdfH = Math.round(sh / scale)
          if (this.selfSignFirst && !this.selfSigned) {
            const newSelf = { page: this.currentPage, x: pdfX, y: pdfY, w: pdfW, h: pdfH, showName: this.addNameChecked === true }
            this.selfWidgets.push(newSelf)
            this.selectedSelfWidget = newSelf
            this.selectedWidget = null
          } else {
            const assignId = this.selectedBuyerIds[0]
            const newWidget = { page: this.currentPage, x: pdfX, y: pdfY, w: pdfW, h: pdfH, buyer_id: assignId, showName: this.addNameChecked === true }
            this.widgets.push(newWidget)
            this.selectedWidget = newWidget
            this.selectedSelfWidget = null
          }
        }
      }
      this.action = null
      this.initialWidgetState = null
    },
    getWidgetStyle(w) {
      const scale = this.viewportScale || 1
      return {
        left: Math.round(w.x * scale) + 'px',
        top: Math.round(w.y * scale) + 'px',
        width: Math.round(w.w * scale) + 'px',
        height: Math.round(w.h * scale) + 'px'
      }
    },
    removeWidget(widget) {
      const i = this.widgets.indexOf(widget)
      if (i > -1) this.widgets.splice(i, 1)
      if (this.selectedWidget === widget) this.selectedWidget = null
    },
    startMoveSelf(widget, e) {
      this.action = 'moveSelf'
      this.selectedSelfWidget = widget
      this.initialSelfState = { ...widget }
      this.selectedWidget = null
      const p = this.toLocal(e)
      this.start = p
      this.current = p
    },
    startResizeSelf(widget, e) {
      this.action = 'resizeSelf'
      this.selectedSelfWidget = widget
      this.initialSelfState = { ...widget }
      this.selectedWidget = null
      const p = this.toLocal(e)
      this.start = p
      this.current = p
    },
    removeSelfWidget(widget) {
      const i = this.selfWidgets.indexOf(widget)
      if (i > -1) this.selfWidgets.splice(i, 1)
      if (this.selectedSelfWidget === widget) this.selectedSelfWidget = null
    },
    async ensureSwal() {
      if (window.Swal) return window.Swal
      await new Promise((resolve, reject) => {
        const script = document.createElement('script')
        script.src = 'https://cdn.jsdelivr.net/npm/sweetalert2@11'
        script.onload = resolve
        script.onerror = reject
        document.head.appendChild(script)
      })
      return window.Swal
    },
    copyWidgetAllPages(widget) {
      if (!widget) return
      this.ensureSwal().then((Swal) => {
        Swal.fire({
          title: 'Feld kopieren',
          text: 'Ausgewähltes Feld auf die aktuelle Seite oder auf alle Seiten kopieren?',
          icon: 'question',
          showCancelButton: true,
          showDenyButton: true,
          confirmButtonText: 'Alle Seiten',
          denyButtonText: 'Aktuelle Seite',
          cancelButtonText: 'Abbrechen'
        }).then(result => {
          if (result.isConfirmed) {
            for (let i = 1; i <= this.totalPages; i++) {
              if (i === widget.page) continue
              this.widgets.push({ page: i, x: widget.x, y: widget.y, w: widget.w, h: widget.h, buyer_id: widget.buyer_id, showName: widget.showName === true })
            }
            Swal.fire({ icon: 'success', text: `Feld zu allen ${this.totalPages} Seiten hinzugefügt.`, showConfirmButton: false, timer: 1000 })
          } else if (result.isDenied) {
            const dup = { page: this.currentPage, x: widget.x, y: widget.y, w: widget.w, h: widget.h, buyer_id: widget.buyer_id, showName: widget.showName === true }
            dup.x = dup.x + 5
            dup.y = dup.y + 5
            this.widgets.push(dup)
            Swal.fire({ icon: 'success', text: 'Feld auf der aktuellen Seite hinzugefügt.', showConfirmButton: false, timer: 1000 })
          }
        })
      })
    },
    copySelfWidgetAllPages(sw) {
      if (!sw) return
      this.ensureSwal().then((Swal) => {
        if (this.selfSigned) {
          Swal.fire(this.$t('documents.errorNotAllowed'), this.$t('documents.errorSelfSignCompleted'), 'info')
          return
        }
        Swal.fire({
          title: 'Signaturfeld kopieren',
          text: 'Auf die aktuelle Seite oder auf alle Seiten kopieren?',
          icon: 'question',
          showCancelButton: true,
          showDenyButton: true,
          confirmButtonText: 'Alle Seiten',
          denyButtonText: 'Aktuelle Seite',
          cancelButtonText: 'Abbrechen'
        }).then(result => {
          if (result.isConfirmed) {
            for (let i = 1; i <= this.totalPages; i++) {
              if (i === sw.page) continue
              this.selfWidgets.push({ page: i, x: sw.x, y: sw.y, w: sw.w, h: sw.h, showName: sw.showName === true })
            }
            Swal.fire({ icon: 'success', text: `Signaturfeld auf alle ${this.totalPages} Seiten kopiert.`, showConfirmButton: false, timer: 1000 })
          } else if (result.isDenied) {
            const dup = { page: this.currentPage, x: sw.x, y: sw.y, w: sw.w, h: sw.h, showName: sw.showName === true }
            dup.x = dup.x + 5
            dup.y = dup.y + 5
            this.selfWidgets.push(dup)
            Swal.fire({ icon: 'success', text: 'Signaturfeld auf die aktuelle Seite kopiert.', showConfirmButton: false, timer: 1000 })
          }
        })
      })
    },
    copyAllToAllPages() {
      const currentWidgets = this.currentPageWidgets;
      const currentSelfWidgets = this.currentPageSelfWidgets;

      if (currentWidgets.length === 0 && currentSelfWidgets.length === 0) {
        this.ensureSwal().then(Swal => Swal.fire({
          title: 'Keine Felder gefunden',
          text: 'Es gibt keine Felder auf dieser Seite zum Kopieren.',
          icon: 'info',
          confirmButtonColor: '#003366'
        }));
        return;
      }

      this.ensureSwal().then(Swal => {
        Swal.fire({
          title: 'Alle Felder auf alle Seiten kopieren',
          text: `Sind Sie sicher, dass Sie alle Felder von Seite ${this.currentPage} auf alle anderen ${this.totalPages} Seiten kopieren möchten?`,
          icon: 'question',
          showCancelButton: true,
          confirmButtonText: 'Ja, auf alle kopieren',
          cancelButtonText: 'Abbrechen',
          confirmButtonColor: '#003366'
        }).then(result => {
          if (result.isConfirmed) {
            for (let pageNum = 1; pageNum <= this.totalPages; pageNum++) {
              if (pageNum === this.currentPage) continue;
              
              currentWidgets.forEach(w => {
                 this.widgets.push({ ...w, page: pageNum });
              });
              
              currentSelfWidgets.forEach(sw => {
                 this.selfWidgets.push({ ...sw, page: pageNum });
              });
            }
            Swal.fire({ 
              icon: 'success', 
              title: 'Erfolgreich kopiert', 
              text: `Alle Felder von Seite ${this.currentPage} wurden auf alle Seiten übertragen.`, 
              showConfirmButton: false, 
              timer: 1500 
            });
          }
        });
      });
    },
    toggleName(widget) { widget.showName = !widget.showName },
    removeName(widget) { widget.showName = false },
    toggleNameSelf(widget) { widget.showName = !widget.showName },
    removeNameSelf(widget) { widget.showName = false },
    clearAll() {
      if (confirm('Alle Felder löschen?')) {
        this.widgets = []
        this.selectedWidget = null
        if (this.selfSignFirst && !this.selfSigned) {
          this.selfWidgets = []
          this.selectedSelfWidget = null
        }
      }
    },
    selfSignNow() {
      if (this.selfWidgets.length === 0) return
      this.selfSigning = true
      axios.post('/documents/digital-sign/self-sign', {
        pdf_base64: this.pdfBlobContainer,
        selfWidgets: this.selfWidgets,
        pages: this.totalPages
      }).then(res => {
        this.selfSigning = false
        this.selfDocId = res.data?.docId || null
        this.selfSignUrl = res.data?.signUrl || null
        if (this.selfSignUrl) {
          this.selfShowModal = true
        } else {
          this.ensureSwal().then(Swal => Swal.fire(this.$t('actions.error'), this.$t('documents.errorCouldNotStartSelfSign'), 'error'))
        }
      }).catch(e => {
        this.selfSigning = false
        // Der Server kann eine sprechende Meldung liefern (etwa die
        // Groessengrenze fuer zu signierende Dokumente, 422) — sie hat Vorrang
        // vor dem allgemeinen Text, sonst sieht der Berater nur "Fehler".
        const meldung = e?.response?.data?.message || this.$t('documents.errorCouldNotStartSelfSign')
        this.ensureSwal().then(Swal => Swal.fire(this.$t('actions.error'), meldung, 'error'))
      })
    },
    checkSelfComplete(silent = false) {
      if (!this.selfDocId) return
      axios.post('/documents/digital-sign/self-fetch', { docId: this.selfDocId }).then(async res => {
        if (res.data?.ok && res.data?.pdf) {
          this.stopSelfPolling()
          this.pdfBlobContainer = res.data.pdf
          this.selfWidgets = []
          this.selectedSelfWidget = null
          this.selfSigned = true
          this.selfShowModal = false
          this.selfSignAt = new Date().toISOString()
          await this.loadPdf(res.data.pdf)
          this.ensureSwal().then(Swal => Swal.fire({ icon: 'success', text: 'Eigene Signatur abgeschlossen – PDF geladen'}))
        } else {
          if (!silent) {
            this.ensureSwal().then(Swal => Swal.fire({ icon: 'info', text: 'Noch nicht abgeschlossen.'}))
          }
        }
      }).catch(() => {
        if (!silent) {
          this.ensureSwal().then(Swal => Swal.fire(this.$t('actions.error'), this.$t('documents.errorCheckingStatus'), 'error'))
        }
      })
    },
    startSelfPolling() {
      this.stopSelfPolling();
      const interval = (parseInt(this.$page.props.check_sign_complete_or_not_in_second) || 2) * 1000;
      this.selfPollingInterval = setInterval(() => {
        this.checkSelfComplete(true);
      }, interval);
    },

    stopSelfPolling() {
      if (this.selfPollingInterval) {
        clearInterval(this.selfPollingInterval);
        this.selfPollingInterval = null;
      }
    },
    send() {
      if (this.isAdminSignOnly) {
          if (!this.selfSigned) {
              this.ensureSwal().then(Swal => Swal.fire(this.$t('actions.error'), this.$t('documents.errorCompleteSignatureFirst'), 'error'));
              return;
          }
          this.$emit('done', {
              digital_sign_json: {
                  docId: this.selfDocId,
                  is_self_sign: 'Yes',
                  self_sign_at: this.selfSignAt,
                  self_sign_user_id: this.authUserId,
                  buyer_sign_json: [] 
              },
              pdf_base64_signed: this.pdfBlobContainer
          });
          return;
      }

      if (this.selectedBuyerIds.length === 0) {
        this.ensureSwal().then(Swal => Swal.fire(this.$t('actions.error'), this.$t('documents.errorSelectBuyer'), 'error'))
        return
      }
      if (!this.allAssigned) {
        this.ensureSwal().then(Swal => Swal.fire(this.$t('actions.error'), this.$t('documents.errorAssignBoxToBuyer'), 'error'))
        return
      }
      const activeBuyerIds = this.selectedBuyerIds.filter(id => this.widgets.some(w => w.buyer_id === id));
      
      this.sending = true
      axios.post('/documents/digital-sign/send-multi', {
        pdf_base64: this.pdfBlobContainer,
        widgets: this.widgets,
        buyers: activeBuyerIds,
        filename: this.fileName
      }).then(res => {
        this.sending = false
        console.log('OpenSign creation response:', res.data);
        const finalDocId = res.data.docId || res.data.doc_id || res.data.objectId;
        if (!finalDocId) {
            console.error('No docId found in response!', res.data);
            this.ensureSwal().then(Swal => Swal.fire(this.$t('common.warning'), this.$t('documents.api_id_not_returned'), 'warning'));
        }
        let digital_sign_json = {
            docId: finalDocId,
            is_self_sign: this.selfSigned ? 'Yes' : 'No',
            self_sign_at: this.selfSigned ? this.selfSignAt : null,
            self_sign_user_id: this.selfSigned ? this.authUserId : null,
            buyer_sign_json: res.data.buyer_sign_json || []
        };
        this.$emit('done', {
            digital_sign_json,
            pdf_base64_signed: this.pdfBlobContainer 
        });
      }).catch(e => {
        this.sending = false
        // s. oben: eine sprechende Server-Meldung hat Vorrang.
        const meldung = e?.response?.data?.message || this.$t('documents.errorSendingDocument')
        this.ensureSwal().then(Swal => Swal.fire(this.$t('actions.error'), meldung, 'error'))
      })
    }
  }
})
</script>

<style scoped>
.digital-sign-config-wrapper {
  z-index: 1050 !important;
}
</style>

<style>
.swal2-container {
  z-index: 99999 !important;
}
</style>
